import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ConverterCapabilities, ConverterMode, FileCategory } from '../core/types';
import { createImageThumbnail } from '../history/thumbnail';
import { AccountMenu } from '../ui/AccountMenu';
import { Dropzone } from '../ui/Dropzone';
import { Footer } from '../ui/Footer';
import { Header, type CategoryFilter, type WorkbenchView } from '../ui/Header';
import { HistoryView } from '../ui/HistoryView';
import { TaskPanel, formatBytes } from '../ui/TaskPanel';
import { ToastStack, useToasts } from '../ui/Toast';
import { consumeAuthError } from './auth-error';
import { useAccount } from './hooks/useAccount';
import { useConversions } from './hooks/useConversions';
import { useHistory } from './hooks/useHistory';
import { useUsage } from './hooks/useUsage';
import './styles.css';

const CATEGORY_TITLES: Record<FileCategory, string> = {
  image: '图片转换',
  document: '文档与结构化数据',
  model: '3D 模型',
  archive: '压缩包',
  other: '其他文件',
};

const CATEGORY_HINTS: Record<FileCategory, string> = {
  image: 'PNG / JPG / WebP / AVIF / SVG，浏览器本地编解码',
  document: 'TXT ↔ MD、CSV ↔ JSON、JSON ↔ YAML / XML、PDF / DOCX / XLSX / XLS → 文本与表格',
  model: 'GLB / GLTF / OBJ / STL，规划中',
  archive: '仅识别，不执行解压',
  other: '保持原样识别',
};

export function App() {
  const { toasts, notify, dismiss } = useToasts();
  const history = useHistory();
  const account = useAccount();
  const usage = useUsage();
  const [view, setView] = useState<WorkbenchView>('workbench');
  const [category, setCategory] = useState<CategoryFilter>('all');
  const [accountOpen, setAccountOpen] = useState(false);

  const conversions = useConversions({
    repository: history.repository,
    notify,
    createThumbnail: createImageThumbnail,
    onUsageChange: usage.refresh,
  });

  // 账号中心回调失败时会把 ?auth_error=<code> 带回首页：翻译成人话、清理地址栏并刷新登录态
  useEffect(() => {
    const info = consumeAuthError(window.location.search, (nextSearch) => {
      window.history.replaceState({}, '', `${window.location.pathname}${nextSearch}`);
    });
    if (!info) return;
    notify({ tone: 'error', title: '登录未完成', description: info.message });
    setAccountOpen(true);
    void account.refresh();
    // 仅在首次挂载时处理回调错误
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const availableCategories = useMemo(() => {
    const set = new Set<FileCategory>();
    for (const capability of conversions.capabilities) set.add(capability.category);
    return [...set];
  }, [conversions.capabilities]);

  const visibleCapabilities = useMemo(() => {
    if (category === 'all') return conversions.capabilities;
    return conversions.capabilities.filter((capability) => capability.category === category);
  }, [category, conversions.capabilities]);

  const formatOptions = useMemo(
    () =>
      conversions.targets.map((format) => {
        const hint = conversions.targetHints[format];
        return {
          format,
          available: isTargetAvailable(format, conversions.capabilities),
          expectedFidelity: expectedFidelityFor(format),
          costHint: hint ? describeCostHint(hint) : undefined,
        };
      }),
    [conversions.capabilities, conversions.targetHints, conversions.targets],
  );

  const handleSelect = useCallback(
    (file: File) => {
      setView('workbench');
      void conversions.selectFile(file);
    },
    [conversions],
  );

  return (
    <div className="app">
      <Header
        view={view}
        category={category}
        availableCategories={availableCategories}
        onCategory={setCategory}
        onView={setView}
        onAccount={() => setAccountOpen(true)}
        authenticated={account.authenticated}
        totalPoints={account.credits?.balance ?? null}
        displayName={account.session?.user?.displayName ?? null}
      />

      <main className="shell" style={{ flex: 1 }}>
        {view === 'history' ? (
          <HistoryView history={history} onReDownload={conversions.state.result ? conversions.download : undefined} />
        ) : (
          <>
            <section className="hero">
              <div className="kicker">YOGO TOOLS · CONVERT</div>
              <h1>文件转换，尽可能不让数据离开你的设备。</h1>
              <p className="lede">
                识别真实格式、在后台线程完成转换、逐条列出保真度与丢失项。本地转换每次 1 分；扫描件与图片可选 AI 识别（每次 2 分）——
                只有选择 AI 通道时，文件页面才会离开你的设备。
              </p>
              <div className="badges">
                <span className="badge-chip">本地优先 · 源文件不上传</span>
                <span className="badge-chip">本地 1 分 / AI 2 分 · 未登录每天 5 次免费</span>
                <span className="badge-chip">保真度 L0–L3 透明可见</span>
                <span className="badge-chip">记录只存元数据与缩略图</span>
              </div>
            </section>

            <section className="workspace" style={{ marginTop: 22 }}>
              <div className="stack">
                <Dropzone onSelect={handleSelect} disabled={conversions.state.status === 'converting'} />
                <TaskPanel
                  state={conversions.state}
                  options={formatOptions}
                  channels={conversions.candidates}
                  pricing={usage.usage?.pricing ?? null}
                  authenticated={account.authenticated}
                  balance={usage.usage?.credits?.balance ?? account.credits?.balance ?? null}
                  freeRemaining={usage.usage?.anonymous.remaining ?? null}
                  aiConsent={conversions.aiConsent}
                  onAiConsentChange={conversions.setAiConsent}
                  onChannelChange={conversions.setChannel}
                  quality={conversions.quality}
                  onQualityChange={conversions.setQuality}
                  onTargetChange={conversions.setTargetFormat}
                  onStart={() => void conversions.start()}
                  onCancel={conversions.cancel}
                  onDownload={conversions.download}
                  onReset={conversions.reset}
                />
              </div>

              <div className="stack">
                <div className="card">
                  <div className="card-head">
                    <span className="card-title">可用能力</span>
                    <span className="spacer" />
                    <span className="card-hint">来自引擎注册表</span>
                  </div>
                  <div className="cards-grid">
                    {visibleCapabilities.map((capability) => (
                      <CapabilityCard key={capability.id} capability={capability} />
                    ))}
                    {visibleCapabilities.length === 0 ? (
                      <div className="empty">
                        {CATEGORY_TITLES[category === 'all' ? 'other' : category]}
                        <br />
                        <span className="card-hint">{CATEGORY_HINTS[category === 'all' ? 'other' : category]}</span>
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="card">
                  <div className="card-head">
                    <span className="card-title">为什么可信</span>
                  </div>
                  <div className="stack" style={{ gap: 10 }}>
                    <AssuranceRow
                      title="本地优先"
                      body="图片与文档转换在浏览器 Worker 中完成，源文件不经过任何服务器。"
                    />
                    <AssuranceRow
                      title="保真度透明"
                      body="每次都给出 L0–L3 等级与「保留 / 改变 / 丢失」清单；声称无损的路径必须通过像素或结构校验。"
                    />
                    <AssuranceRow
                      title="记录可控"
                      body="转换记录只写元数据与缩略图，不保存原文件与产物，随时可一键清空。"
                    />
                  </div>
                </div>
              </div>
            </section>
          </>
        )}
      </main>

      <Footer />
      <ToastStack toasts={toasts} onDismiss={dismiss} />
      <AccountMenu
        open={accountOpen}
        onClose={() => setAccountOpen(false)}
        account={account}
        usage={usage.usage}
        onRefreshUsage={usage.refresh}
      />
    </div>
  );
}

function describeCostHint(hint: { modes: ConverterMode[]; estimatedCost: number }): string {
  if (hint.modes.length > 1) return `本地 1 分 / AI 2 分`;
  return hint.modes[0] === 'ai' ? `AI 识别 ${hint.estimatedCost} 分` : `本地 ${hint.estimatedCost} 分`;
}

function CapabilityCard({ capability }: { capability: ConverterCapabilities }) {
  const tagLabel =
    capability.available === false
      ? '不可用'
      : capability.mode === 'ai'
        ? `AI 识别 · ${capability.estimatedCost} 分`
        : `本地 · ${capability.estimatedCost} 分`;
  return (
    <div className="tool-card">
      <div className="row">
        <span style={{ fontWeight: 500 }}>{CATEGORY_TITLES[capability.category]}</span>
        <span className="spacer" />
        <span className="tag">{tagLabel}</span>
      </div>
      <p className="card-hint" style={{ marginTop: 6 }}>
        {CATEGORY_HINTS[capability.category]}
      </p>
      <p className="card-hint" style={{ marginTop: 8 }}>
        源：{capability.sourceFormats.map((item) => item.toUpperCase()).join(' / ')}
      </p>
      <p className="card-hint">
        目标：{capability.targetFormats.map((item) => item.toUpperCase()).join(' / ') || '暂无'}
      </p>
      {capability.unavailableReason ? <p className="card-hint">限制：{capability.unavailableReason}</p> : null}
    </div>
  );
}

function AssuranceRow({ title, body }: { title: string; body: string }) {
  return (
    <div>
      <div style={{ fontWeight: 500 }}>{title}</div>
      <div className="card-hint">{body}</div>
    </div>
  );
}

function isTargetAvailable(format: string, capabilities: ConverterCapabilities[]): boolean {
  return capabilities.some((capability) => capability.available !== false && capability.targetFormats.includes(format));
}

function expectedFidelityFor(format: string): 'L2' | 'L3' {
  return format === 'png' ? 'L2' : 'L3';
}

export { formatBytes };
